import React from "react";
import ReactDOM from "react-dom/client";
import { TYPE_SCORE } from "../../enums.js";

/** @typedef {import('../../models/CfcWodModel').CfcWodItem} CfcWodItem */
/** @typedef {import('../../services/CfcScoreService').CfcScoresOfRegistration} CfcScoresOfRegistration */
/** @typedef {import('../../controllers/CfcScoreController').ResultLeaderboardApiObj} ResultLeaderboardApiObj */
/** @typedef {import('../../models/CfcCategoryModel').CfcCategoryItem} CfcCategoryItem */

/**
 * @typedef {object} CfcLeaderboardProps
 * @property {string} cfcTournamentId - ID de la compétition
 * @property {string} encryptedCfcTournamentId - ID chiffré de la compétition
 * @property {string} typeScore - Type de score (ex: "total", "wod1", "wod2", etc.)
 * @property {string} cfcCategoryId - ID de la catégorie (optionnel)
 * @property {string} cfcSubCategoryId - ID de la sous-catégorie (optionnel)
 */

class CfcLeaderboard extends React.Component {
  /**
   * @param {CfcLeaderboardProps} props - Propriétés
   */
  constructor(props) {
    super(props);
    this.state = {
      cfcTournamentId: props.cfcTournamentId,
      encryptedCfcTournamentId: props.encryptedCfcTournamentId,
      typeScore: props.typeScore,
      cfcCategories: /** @type {Array<CfcCategoryItem>} */ ([]),
      cfcCategoryId: props.cfcCategoryId || null,
      cfcSubCategoryId: props.cfcSubCategoryId || null,
      selectedWodId: "",
      cfcWods: /** @type {Array<CfcWodItem>} */ ([]),
      cfcScoresOfLogic: /** @type {Array<CfcScoresOfRegistration>} */ ([]),
      filteredCfcScoresOfLogic: /** @type {Array<CfcScoresOfRegistration>} */ ([]),
      coordinates: /** @type {Array<string>} */ ([]),
      selectedCoordinate: "",
      searchValue: "",
      visibleWodCount: 1,
      wodStartIndex: 0,
      wodSort: {
        wodId: null,
        direction: null
      },
      tableWidth: 0,
      hasIntermediateCfcScore: false,
      hasScaledCfcScore: false,
      hasFinalWod: false
    };
    this.tableContainerRef = React.createRef();
    this.updateVisibleWods = this.updateVisibleWods.bind(this);
  }

  /**
   * Fonction spécifique à ReactJS pour charger le composant
   */
  async componentDidMount() {
    const resultCfcLeaderboard = await this.fetchCfcLeaderboard();
    const resultCfcCategories = await this.fetchCfcCategories();

    this.setState(
      {
        cfcWods: resultCfcLeaderboard.cfcWods,
        cfcScoresOfLogic: resultCfcLeaderboard.cfcScoresOfLogic,
        filteredCfcScoresOfLogic: resultCfcLeaderboard.cfcScoresOfLogic,
        coordinates: this.getCoordinates(resultCfcLeaderboard.cfcScoresOfLogic),
        cfcCategories: resultCfcCategories,
        hasIntermediateCfcScore: resultCfcLeaderboard.hasIntermediateCfcScore,
        hasScaledCfcScore: resultCfcLeaderboard.hasScaledCfcScore,
        hasFinalWod: resultCfcLeaderboard.hasFinalWod
      },
      this.updateVisibleWods
    );

    window.addEventListener("resize", this.updateVisibleWods);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateVisibleWods);
  }

  /**
   * Permet de récupérer l'objet du leaderboard
   * @returns {Promise<ResultLeaderboardApiObj>} L'objet du leaderboard
   */
  async fetchCfcLeaderboard() {
    const cfcCategoryId = this.state.cfcCategoryId;
    const cfcSubCategoryId = this.state.cfcSubCategoryId;

    let apiUrl = `/api/cfc-score/index/${this.state.encryptedCfcTournamentId}?typescore=${this.state.typeScore}`;

    if (cfcCategoryId) {
      apiUrl += `&cfcCategoryId=${cfcCategoryId}`;
    }

    if (cfcSubCategoryId) {
      apiUrl += `&cfcSubCategoryId=${cfcSubCategoryId}`;
    }
    const response = await fetch(apiUrl, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Permet de récupérer la liste des catégories d'une compétition
   * @returns {Promise<Array<CfcCategoryItem>>} - Liste des catégories
   */
  async fetchCfcCategories() {
    var response = await fetch(`/api/cfc-category/search?cfcTournamentId=${this.state.cfcTournamentId}`, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Recharge le classement avec les filtres sélectionnés.
   * @returns {Promise<void>}
   */
  async refreshLeaderboard() {
    const resultCfcLeaderboard = await this.fetchCfcLeaderboard();

    this.setState(
      {
        cfcWods: resultCfcLeaderboard.cfcWods,
        cfcScoresOfLogic: resultCfcLeaderboard.cfcScoresOfLogic,
        wodStartIndex: 0
      },
      () => {
        this.filterCfcScoresOfLogic();
        this.updateVisibleWods();
      }
    );
  }

  /**
   * @param {import('react').ChangeEvent} event - Changement du type de classement
   */
  onChangeOnType(event) {
    const target = /** @type {HTMLSelectElement} */ (event.target);

    this.setState({ typeScore: target.value }, () => this.refreshLeaderboard());
  }

  /**
   * @param {import('react').ChangeEvent} event - Changement de catégorie ou sous-catégorie
   */
  onChangeOnCategory(event) {
    const target = /** @type {HTMLSelectElement} */ (event.target);
    const [cfcCategoryId, cfcSubCategoryId = null] = target.value.split("-");

    this.setState({ cfcCategoryId, cfcSubCategoryId, selectedWodId: "" }, () => this.refreshLeaderboard());
  }

  /**
   * @param {import('react').ChangeEvent} event - Changement du WOD sélectionné dans le filtre
   */
  onChangeOnWod(event) {
    const target = /** @type {HTMLSelectElement} */ (event.target);

    this.setState({ selectedWodId: target.value }, this.updateVisibleWods);
  }

  /**
   * Lorsqu'on saisit une valeur dans le champ de recherche, on filtre les scores par enregistrement
   * @param {import('react').ChangeEvent} event - Évenement associé au changement de valeur dans le champ de recherche
   */
  onChangeOnSearch = event => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    const searchValue = target.value.trim().toLowerCase();

    this.setState({ searchValue }, () => this.filterCfcScoresOfLogic());
  };

  /**
   * @param {import('react').ChangeEvent} event - Évenement associé au changement de coordonnée
   */
  onChangeOnCoordinate(event) {
    const target = /** @type {HTMLSelectElement} */ (event.target);

    this.setState({ selectedCoordinate: target.value }, () => this.filterCfcScoresOfLogic());
  }

  /**
   * Filtre les scores du classement en fonction de la valeur de recherche et de la coordonnée sélectionnée.
   * Met à jour l'état filteredCfcScoresOfLogic avec les scores filtrés.
   * @returns {void}
   */
  filterCfcScoresOfLogic() {
    const { cfcScoresOfLogic, searchValue, selectedCoordinate } = this.state;
    const filteredCfcScoresOfLogic = cfcScoresOfLogic.filter(cfcScore => {
      const matchesSearch = !searchValue || [cfcScore.box, cfcScore.team_name, cfcScore.teams].filter(Boolean).some(value => String(value).toLowerCase().includes(searchValue));
      const matchesCoordinate = !selectedCoordinate || cfcScore.box === selectedCoordinate;

      return matchesSearch && matchesCoordinate;
    });

    this.setState({ filteredCfcScoresOfLogic });
  }

  /**
   * @param {Array<CfcScoresOfRegistration>} cfcScoresOfLogic - Scores du classement
   * @returns {Array<string>}
   */
  getCoordinates(cfcScoresOfLogic) {
    return [...new Set(cfcScoresOfLogic.map(cfcScore => cfcScore.box).filter(Boolean))].sort();
  }

  /**
   * Met à jour le nombre de WODs visibles en fonction de la largeur du conteneur et ajuste l'index de départ si nécessaire.
   */
  updateVisibleWods() {
    const containerWidth = this.tableContainerRef.current?.clientWidth || 0;
    const { teamColumnWidth, pointsColumnWidth } = this.getTableColumnWidths(containerWidth);
    const visibleWodCount = Math.max(1, Math.floor((containerWidth - teamColumnWidth - pointsColumnWidth) / 140));
    const maxStartIndex = Math.max(0, this.state.cfcWods.length - visibleWodCount);

    this.setState({
      visibleWodCount,
      wodStartIndex: Math.min(this.state.wodStartIndex, maxStartIndex),
      tableWidth: containerWidth
    });
  }

  /**
   * @param {number} tableWidth - Largeur disponible pour le tableau
   * @returns {{teamColumnWidth: number, pointsColumnWidth: number}}
   */
  getTableColumnWidths(tableWidth) {
    return {
      teamColumnWidth: Math.min(220, Math.floor(tableWidth * 0.55)),
      pointsColumnWidth: Math.min(70, Math.floor(tableWidth * 0.2))
    };
  }

  /**
   * Retourne les index (dans cfcWods) des WODs à afficher dans le tableau.
   * @returns {Array<number>}
   */
  getVisibleWodIndexes() {
    if (this.state.selectedWodId) {
      const selectedIndex = this.state.cfcWods.findIndex(cfcWod => String(cfcWod.id) === String(this.state.selectedWodId));

      return selectedIndex === -1 ? [] : [selectedIndex];
    }

    const indexes = [];

    for (let index = this.state.wodStartIndex; index < this.state.wodStartIndex + this.state.visibleWodCount && index < this.state.cfcWods.length; index++) {
      indexes.push(index);
    }

    return indexes;
  }

  /**
   * @returns {Array<CfcWodItem>}
   */
  getVisibleWods() {
    return this.getVisibleWodIndexes().map(index => this.state.cfcWods[index]);
  }

  /**
   * Change le sens de tri du WOD sélectionné et désactive le tri après le sens descendant.
   * @param {CfcWodItem} cfcWod - WOD utilisé pour le tri
   */
  onClickOnWodSort(cfcWod) {
    const { wodId, direction } = this.state.wodSort;
    const nextDirection = wodId !== cfcWod.id ? "asc" : direction === "asc" ? "desc" : direction === "desc" ? null : "asc";

    this.setState({
      wodSort: {
        wodId: nextDirection ? cfcWod.id : null,
        direction: nextDirection
      }
    });
  }

  /**
   * Trie les lignes selon le rang obtenu sur le WOD actif.
   * @returns {Array<CfcScoresOfRegistration>}
   */
  getSortedCfcScores() {
    const { wodId, direction } = this.state.wodSort;

    if (!wodId || !direction) {
      return this.state.filteredCfcScoresOfLogic;
    }

    const wodIndex = this.state.cfcWods.findIndex(cfcWod => String(cfcWod.id) === String(wodId));

    if (wodIndex === -1) {
      return this.state.filteredCfcScoresOfLogic;
    }

    return this.state.filteredCfcScoresOfLogic
      .map((cfcScore, index) => ({ cfcScore, index, rank: cfcScore.scores[wodIndex]?.rank }))
      .sort((first, second) => {
        if (first.rank == null) return 1;
        if (second.rank == null) return -1;

        const comparison = first.rank - second.rank;
        return direction === "asc" ? comparison : -comparison || first.index - second.index;
      })
      .map(({ cfcScore }) => cfcScore);
  }

  /**
   * Permet de passer aux WODs précédents
   */
  showPreviousWods() {
    this.setState({ wodStartIndex: Math.max(0, this.state.wodStartIndex - 1) });
  }

  /**
   * Permet de passer aux WODs suivants
   */
  showNextWods() {
    const maxStartIndex = Math.max(0, this.state.cfcWods.length - this.state.visibleWodCount);

    this.setState({ wodStartIndex: Math.min(maxStartIndex, this.state.wodStartIndex + 1) });
  }

  /**
   * Rendu du sélecteur de catégorie et sous-catégorie.
   * @returns {React.ReactNode}
   */
  renderCategorySelect() {
    return (
      <select className="input-group__input" value={this.state.cfcSubCategoryId ? `${this.state.cfcCategoryId}-${this.state.cfcSubCategoryId}` : this.state.cfcCategoryId || ""} onChange={this.onChangeOnCategory.bind(this)}>
        {this.state.cfcCategories.map(cfcCategory => {
          const cfcSubCategories = cfcCategory.cfc_sub_categories || [];

          if (!cfcSubCategories.length) {
            return (
              <option value={cfcCategory.id} key={cfcCategory.id}>
                {cfcCategory.name}
              </option>
            );
          }

          return (
            <optgroup label={cfcCategory.name} key={cfcCategory.id}>
              <option value={cfcCategory.id}>Toutes catégories</option>
              {cfcSubCategories.map(cfcSubCategory => (
                <option value={`${cfcCategory.id}-${cfcSubCategory.id}`} key={cfcSubCategory.id}>
                  {cfcSubCategory.name}
                </option>
              ))}
            </optgroup>
          );
        })}
      </select>
    );
  }

  /**
   * Rendu du sélecteur de WOD, permettant de filtrer le tableau sur un seul WOD de la catégorie sélectionnée.
   * @returns {React.ReactNode}
   */
  renderWodSelect() {
    return (
      <select className="input-group__input" value={this.state.selectedWodId} onChange={this.onChangeOnWod.bind(this)}>
        <option value="">Tous les wods</option>
        {this.state.cfcWods.map(cfcWod => (
          <option value={cfcWod.id} key={cfcWod.id}>
            {cfcWod.name}
          </option>
        ))}
      </select>
    );
  }

  render() {
    const visibleWods = this.getVisibleWods();
    const visibleWodIndexes = this.getVisibleWodIndexes();
    const sortedCfcScores = this.getSortedCfcScores();
    const canShowPreviousWods = !this.state.selectedWodId && this.state.wodStartIndex > 0;
    const canShowNextWods = !this.state.selectedWodId && this.state.wodStartIndex + this.state.visibleWodCount < this.state.cfcWods.length;
    const { teamColumnWidth, pointsColumnWidth } = this.getTableColumnWidths(this.state.tableWidth);
    const wodColumnWidth = Math.max(0, (this.state.tableWidth - teamColumnWidth - pointsColumnWidth) / Math.max(1, visibleWods.length));

    return (
      <div style={{ display: "grid", boxSizing: "border-box", gridTemplateColumns: "minmax(0, 1fr)", gridTemplateAreas: "auto auto 1fr", gap: "8px", height: "100%", maxWidth: "100%", overflowX: "hidden", overflowY: "auto" }}>
        <div className="grid grid--has-5-child grid--has-2-child-mobile grid--8-gap no-mobile">
          <input type="text" className="input-group__input" aria-label="Recherche" placeholder="Rechercher..." onChange={this.onChangeOnSearch.bind(this)} />
          <select className="input-group__input" value={this.state.selectedCoordinate} onChange={this.onChangeOnCoordinate.bind(this)}>
            <option value="">Recherche par Box / Club</option>
            {this.state.coordinates.map(coordinate => (
              <option value={coordinate} key={coordinate}>
                {coordinate}
              </option>
            ))}
          </select>
          <select className="input-group__input" value={this.state.typeScore} onChange={this.onChangeOnType.bind(this)}>
            <option value={TYPE_SCORE.GENERAL}>Classement général</option>
            {this.state.hasScaledCfcScore && <option value={TYPE_SCORE.SCALED}>Classement Scaled</option>}
            {this.state.hasIntermediateCfcScore && <option value={TYPE_SCORE.INTERMEDIATE}>Classement Intermédiaire</option>}
            {this.state.hasFinalWod && <option value={TYPE_SCORE.FINAL}>Classement Final</option>}
          </select>
          {this.renderCategorySelect()}
          {this.renderWodSelect()}
          {(canShowPreviousWods || canShowNextWods) && (
            <div className="flexline flexline--full flexline--margin flexline--no-mobile">
              <button className="btn btn--no-label" disabled={!canShowPreviousWods} onClick={this.showPreviousWods.bind(this)}>
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <button className="btn btn--no-label" disabled={!canShowNextWods} onClick={this.showNextWods.bind(this)}>
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          )}
        </div>
        <div className="table" ref={this.tableContainerRef} style={{ minWidth: 0, overflow: "hidden" }}>
          <table className="table__table table__table--td-bordered table__table--column-bordered table__table--fixed" style={{ width: "100%" }}>
            <thead>
              <tr className="table__tr">
                <th style={{ width: `${teamColumnWidth}px`, overflowWrap: "anywhere" }} className="table__th table__th--align-left table__th--main">
                  <div className="flexline flexline--gap-16 flexline--no-mobile">
                    <p className="paragraph paragraph--no-margin">#</p>
                    <p className="paragraph paragraph--no-margin">Équipe / Athlète</p>
                  </div>
                </th>
                <th style={{ width: `${pointsColumnWidth}px`, overflowWrap: "anywhere" }} className="table__th table__th--align-left table__th--main">
                  Points
                </th>
                {visibleWods.map(cfcWod => (
                  <th key={cfcWod.id} style={{ width: `${wodColumnWidth}px`, overflowWrap: "anywhere" }} className="table__th table__th--main">
                    <p
                      className="paragraph paragraph--no-margin paragraph--bold-600 paragraph--break-space flexline flexline--align-vz flexline--align-hz flexline--gap-4"
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      title="Trier par ce WOD"
                      onClick={this.onClickOnWodSort.bind(this, cfcWod)}
                      onKeyDown={event => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          this.onClickOnWodSort(cfcWod);
                        }
                      }}>
                      {cfcWod.name}
                      <span className="material-symbols-outlined" aria-hidden="true">
                        {this.state.wodSort.wodId === cfcWod.id ? (this.state.wodSort.direction === "asc" ? "arrow_upward" : this.state.wodSort.direction === "desc" ? "arrow_downward" : "") : "unfold_more"}
                      </span>
                    </p>
                    <div className="flexline flexline--space-evenly flexline--gap-4 flexline--no-mobile">
                      <p className="paragraph paragraph--no-margin paragraph--small-font-size">Rank</p>
                      <p className="paragraph paragraph--no-margin paragraph--small-font-size">Point</p>
                      <p className="paragraph paragraph--no-margin paragraph--small-font-size">Score</p>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedCfcScores.map((cfcScore, index) => (
                <tr key={cfcScore.id} className="table__tr">
                  <td className="table__td table__td--align-left">
                    <div className="flexline flexline--gap-16 flexline--no-mobile flexline--align-vz">
                      <div>
                        <p className="paragraph paragraph--no-margin paragraph--bold-600">{index + 1}</p>
                      </div>
                      <div>
                        <div className="flexline flexline--no-mobile flexline--direction-column">
                          {cfcScore.team_name ? (
                            <div>
                              <p className="paragraph paragraph--no-margin paragraph--bold-600 paragraph--break-space">Team {cfcScore.team_name}</p>
                              <p className="paragraph paragraph--no-margin paragraph--grey-600 paragraph--break-space">{cfcScore.teams}</p>
                            </div>
                          ) : (
                            <p className="paragraph paragraph--no-margin paragraph--bold-600">
                              {cfcScore.firstname} {cfcScore.lastname}
                            </p>
                          )}
                          {cfcScore.box && (
                            <p className="paragraph paragraph--small-font-size paragraph--no-margin paragraph--grey-600 paragraph--break-space">
                              <i className="is-capitalize">{cfcScore.box}</i>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="table__td table__td--align-left">{cfcScore.total_score}</td>
                  {visibleWodIndexes
                    .map(wodIndex => cfcScore.scores[wodIndex])
                    .map((scoreObj, index) => (
                      <td key={index} className="table__td table__td--align-left">
                        <div className="flexline flexline--space-evenly flexline--no-mobile flexline--align-vz">
                          <p className="paragraph paragraph--no-margin paragraph--bold-600">{scoreObj.rank}</p>
                          <p className="paragraph paragraph--no-margin paragraph--small-font-size">{scoreObj.points}</p>
                          <div className="flexline flexline--align-vz flexline--gap-4 flexline--no-mobile">
                            <p className="paragraph paragraph--no-margin paragraph--small-font-size">{scoreObj.score}</p>
                            <p className="paragraph paragraph--grey-600 paragraph--no-margin paragraph--small-font-size">({scoreObj.type})</p>
                          </div>
                        </div>
                      </td>
                    ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

var cfcLeaderboardNode = document.getElementById("cfc-leaderboard");
if (cfcLeaderboardNode) {
  var root = ReactDOM.createRoot(cfcLeaderboardNode);
  root.render(<CfcLeaderboard encryptedCfcTournamentId={cfcLeaderboardNode.getAttribute("data-encryptedcfctournamentid")} cfcTournamentId={cfcLeaderboardNode.getAttribute("data-cfctournamentid")} typeScore={cfcLeaderboardNode.getAttribute("data-typescore")} cfcCategoryId={cfcLeaderboardNode.getAttribute("data-cfccategoryid")} cfcSubCategoryId={cfcLeaderboardNode.getAttribute("data-cfcsubcategoryid")} />);
}
